import { IconButton } from '@/shared/ui/primitives';
import type { FC, Ref } from 'react';
import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { SearchIcon } from './icons/SearchIcon';
import { TEST_IDS } from '@/testIds';
import styles from './SearchBar.module.css';

interface SearchBarProps {
  isVisible: boolean;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  onClose: () => void;
  onOpen: () => void;
  triggerRef?: Ref<HTMLButtonElement>;
}

export const SearchBar: FC<SearchBarProps> = ({
  isVisible,
  searchQuery,
  onSearchQueryChange,
  onClose,
  onOpen,
  triggerRef,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isVisible) inputRef.current?.focus({ preventScroll: true });
  }, [isVisible]);

  if (isVisible) {
    return (
      <div className={styles.search} role="search">
        <span className={styles.searchIcon} aria-hidden="true">
          <SearchIcon />
        </span>
        <input
          ref={inputRef}
          type="search"
          aria-label="Search"
          placeholder="Search"
          className={styles.input}
          data-testid={TEST_IDS.searchInput}
          value={searchQuery}
          autoComplete="off"
          enterKeyHint="search"
          onChange={(event) => onSearchQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') onClose();
          }}
        />
        <IconButton
          variant="ghost"
          className={styles.clearButton}
          aria-label={searchQuery ? 'Clear search' : 'Close search'}
          onClick={() => {
            if (searchQuery) {
              onSearchQueryChange('');
              inputRef.current?.focus({ preventScroll: true });
            } else {
              onClose();
            }
          }}
        >
          <X size={18} aria-hidden="true" />
        </IconButton>
      </div>
    );
  }

  return (
    <IconButton
      ref={triggerRef}
      aria-label="Search"
      variant="surface"
      onClick={onOpen}
      data-testid={TEST_IDS.openSearch}
    >
      <SearchIcon />
    </IconButton>
  );
};
